// External Imports
import { Fragment, useContext, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";

// Internal Imports
import { formatCurrency, toTitleCase } from "../../util/format-text";
import { requestAdminAPI } from "../../api/posh-api";
import { isoDateToString } from "../../util/format-date";
import Heading from "../UI/Heading";
import DetailPair from "../UI/DetailPair";
import PersonCard from "../UI/PersonCard";
import { MdCheck, MdClose, MdRefresh } from "react-icons/md";
import ExpenseCard from "./ExpenseCard";
import PaymentCard from "../payments-page/PaymentCard";
import Modal from "../UI/Modal";
import LoadingSpinner from "../UI/LoadingSpinner";
import { endOfDay, startOfDay } from "date-fns";
import AddExpense from "./AddExpense";
import FintableLogo from "../../assets/img/fintable-logo.webp";
import AuditLogs from "../UI/AuditLogs";

/** ========================================================================
 * * Transaction Information Page
 * Renders a page, which displays all information about a transaction.
 * ========================================================================== */
export default function TransactionInfo() {
  // Get ID from URL
  const [searchParams] = useSearchParams();
  const id = searchParams.get("id");

  // Initialize States
  const [transaction, setTransaction] = useState({});
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(false);
  const [addExpense, setAddExpense] = useState(false);

  // =================== FETCH TRANSACTION ===================
  const fetchTransaction = async () => {
    try {
      setLoading(true);
      const response = await requestAdminAPI("fetch", {
        table: "transaction",
        id: id,
        columns: [
          "reconciledBy { name photo id }",
          "expenses: expenses_on_transaction { id dateCreated description dueDate datePaid category amount status }",
          "payments: payments_on_transaction { id dateCreated customer { firstName lastName } amount description dueDate datePaid status }",
        ],
      });
      setTransaction(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== ASSOCIATE EXPENSE ===================
  const associateExpense = async (expenseId) => {
    try {
      setLoading(true);
      await requestAdminAPI("reconcile-transaction", {
        id: transaction.id,
        expenseIds: [expenseId],
      });
      fetchTransaction();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== USE_EFFECT ===================
  useEffect(() => {
    fetchTransaction();
  }, []);

  if (!id || (!loading && (!transaction || !transaction.id)))
    return (
      <div className="w-full h-full p-8 rounded-lg bg-stone-900">
        <p className="text-stone-400">
          Transaction ID Not Provided or Transaction Not Found!
        </p>
      </div>
    );

  return (
    <div className="divider">
      <ReconcileTransactionModal
        open={selected}
        setOpen={setSelected}
        transaction={transaction}
        refresh={fetchTransaction}
      />
      <AddExpense
        open={addExpense}
        setOpen={setAddExpense}
        onComplete={associateExpense}
        defaultPaidVia={transaction.bank === "chase" ? "_zelle" : "_clover"}
        defaultAmount={Math.abs(transaction.amount)}
        defaultDate={isoDateToString(transaction.dateCreated, "yyyy-MM-dd")}
      />
      <div className="container relative grid gap-6 lg:col-span-3">
        <Heading title="Transaction Details" id={transaction?.id}>
          <AuditLogs table="transaction" id={id} />
          <MdRefresh onClick={fetchTransaction} className="icon" />
        </Heading>
        <div className="info lg:grid-cols-3">
          <DetailPair
            label="Date Added"
            value={isoDateToString(transaction?.dateCreated, "MM/dd/yyyy")}
            loading={loading}
          />
          <DetailPair
            label="Date Created"
            value={transaction?.date}
            loading={loading}
          />
          <DetailPair
            label="Bank"
            classes="font-semibold"
            value={transaction?.bank?.toUpperCase()}
            loading={loading}
          />
          <DetailPair
            label="Account"
            value={transaction.account}
            loading={loading}
          />
          <DetailPair
            label="Type"
            value={toTitleCase(transaction.type)}
            loading={loading}
          />
          <DetailPair
            label="Amount"
            classes={
              transaction.type === "expense"
                ? "text-red-500 font-bold text-lg"
                : "text-green-500 font-bold text-lg"
            }
            value={formatCurrency(transaction.amount)}
            loading={loading}
          />
          <div className="lg:col-span-3">
            <DetailPair
              label="Description"
              classes="font-semibold"
              value={transaction?.description}
              loading={loading}
            />
          </div>
        </div>

        {transaction.expenses?.length > 0 && (
          <div className="grid gap-2">
            <Heading
              title="Associated Expenses"
              onClick={() => {}}
              isBackArrow={false}
            />
            <div className="list">
              {transaction.expenses.map((expense, idx) => (
                <ExpenseCard expense={expense} key={idx} />
              ))}
            </div>
          </div>
        )}
        {transaction.payments?.length > 0 && (
          <div className="list">
            {transaction.payments.map((payment, idx) => (
              <PaymentCard payment={payment} key={idx} />
            ))}
          </div>
        )}
        <div
          className="absolute bottom-10 right-10 clickable"
          onClick={() =>
            window.open("https://fintable.io/dash/v2/accounts", "_blank")
          }
        >
          <p className="mb-2 text-xs text-stone-400">Powered By:</p>
          <img
            src={FintableLogo}
            alt="Akoya Logo"
            style={{ filter: "brightness(0) invert(1)" }}
            className="max-w-[100px]"
          />
        </div>
      </div>

      <div className="sidebar">
        <div className="grid gap-8">
          {transaction.reconciledBy && (
            <PersonCard
              label="Reconciled By:"
              name={transaction?.reconciledBy?.name}
              secondary={isoDateToString(transaction.reconciledDate)}
              image={transaction?.reconciledBy?.photo}
              link={`/employees/info?id=${transaction?.reconciledBy?.id}`}
              loading={loading}
            />
          )}
        </div>

        <div className="grid gap-4">
          {!transaction.reconciledById && transaction.type === "expense" && (
            <Fragment>
              <div
                onClick={() => setAddExpense(true)}
                className="btn-secondary"
              >
                Add New Expense
              </div>
              <div onClick={() => setSelected(true)} className="btn-primary">
                Reconcile Expense
              </div>
            </Fragment>
          )}
          {!transaction.reconciledById && transaction.type === "income" && (
            <div onClick={() => setSelected(true)} className="btn-primary">
              Reconcile Payment
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** ========================================================================
 * * RECONCILE TRANSACTION MODAL
 * ========================================================================== */
const ReconcileTransactionModal = ({ open, setOpen, transaction, refresh }) => {
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(1);
  const [suggested, setSuggested] = useState([]);

  // =================== RECONCILE TRANSACTION ===================
  const reconcileTransaction = async () => {
    try {
      setLoading(true);
      const payload = {
        id: transaction.id,
      };
      const ids = [];
      for (let i = 1; i <= count; i++) {
        const input = document.getElementById(`${i}`);
        if (input && input.value) ids.push(input.value);
      }
      if (transaction.type === "expense") payload.expenseIds = ids;
      else payload.paymentIds = ids;
      await requestAdminAPI("reconcile-transaction", payload);
      setOpen(false);
      refresh();
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== SUGGESTED EXPENSES ===================
  const findRecord = async () => {
    try {
      setLoading(true);
      let transactionDate = new Date(transaction.date);
      transactionDate.setDate(transactionDate.getDate() + 1);
      transactionDate = endOfDay(transactionDate);
      let previousDate = new Date(transaction.date);
      previousDate.setDate(previousDate.getDate() - 5);
      previousDate = startOfDay(previousDate);

      const response = await requestAdminAPI("filter", {
        table: transaction.type === "expense" ? "expense" : "payment",
        filters: [
          `amount: { eq: ${Math.abs(transaction.amount)} }`,
          `datePaid: { ge: "${previousDate.toISOString()}" }`,
          `datePaid: { le: "${transactionDate.toISOString()}" }`,
          `status: { eq: "succeeded" }`,
        ],
      });

      setSuggested(response);
      setLoading(false);
    } catch (err) {
      console.error(err);
      setLoading(false);
    }
  };

  // =================== SELECT RECORD ===================
  const selectRecord = (id) => {
    const input = document.getElementById(`${count}`);
    if (input) input.value = id;
    setCount(count + 1);
  };

  useEffect(() => {
    if (open) findRecord();
  }, [open]);

  return (
    <Modal open={open} setOpen={setOpen}>
      <div className="modal max-w-[900px] text-sm">
        <Heading
          title="Reconcile Transaction"
          id={transaction.id}
          text="Search Expenses/Payments to find the relevant records and enter their IDs below."
          isBackArrow={false}
        >
          <MdClose onClick={() => setOpen(false)} className="icon" />
        </Heading>
        {loading ? (
          <div className="w-full h-24 rounded-md bg-stone-800 animate-pulse"></div>
        ) : (
          suggested.length > 0 && (
            <div className="grid gap-2 p-4 rounded-md bg-stone-800">
              <p className="font-semibold text-stone-200">
                {transaction.type === "expense"
                  ? "Suggested Expenses"
                  : "Suggested Payments"}
                :
              </p>
              <div className="list">
                {suggested.map((record, idx) => (
                  <div key={idx} className="flex items-center space-x-4">
                    <div className="grid w-full p-2 text-sm border rounded-md lg:grid-cols-6 border-stone-600">
                      <p className="text-stone-400">
                        {isoDateToString(record.datePaid)}
                      </p>
                      <p className="lg:col-span-4">{record.description}</p>
                      <p className="text-stone-200">
                        {formatCurrency(record.amount)}
                      </p>
                    </div>
                    <MdCheck
                      className="icon"
                      onClick={() => selectRecord(record.id)}
                    />
                  </div>
                ))}
              </div>
            </div>
          )
        )}
        {Array.from({ length: count }, (_, i) => i + 1).map((c) => (
          <div className="flex items-end space-x-4">
            <div className="w-full">
              <DetailPair
                label={
                  (transaction.type === "expense"
                    ? "Expense ID"
                    : "Payment ID") + ` #${c}`
                }
                edit={true}
                inputOptions={{
                  id: `${c}`,
                  type: "text",
                }}
                loading={loading}
              />
            </div>
            {c === count ? (
              <div
                onClick={() => setCount(count + 1)}
                className="text-sm btn-secondary"
              >
                Add
              </div>
            ) : (
              <div
                onClick={() => setCount(count - 1)}
                className="text-sm btn-warning"
              >
                Remove
              </div>
            )}
          </div>
        ))}
        {loading ? (
          <LoadingSpinner />
        ) : (
          <div onClick={reconcileTransaction} className="btn-primary">
            Confirm
          </div>
        )}
      </div>
    </Modal>
  );
};
